import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtMon, fmtDate, apiErr } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeftIcon, EyeIcon } from "@phosphor-icons/react";
import { PurchaseInvoiceViewer, openAuthPdf } from "@/pages/Purchases";
import { ESTADO_PAGO } from "@/pages/_purchases/PagoCompra";

const SIN = "sin";
const inp = "bg-app border border-border rounded-md px-2 py-1.5 text-xs";

/**
 * Perfil del proveedor: vista consolidada y una por subcuenta de procedencia (y las facturas «Pendientes de
 * clasificación»). Compras, pagos y notas de crédito con filtros por fecha, sucursal, moneda y estado; totales por
 * moneda con su equivalente en lempiras (nunca se suman monedas distintas). Las subcuentas creadas desde Compras
 * aparecen solas.
 */
export default function SupplierProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const esAdmin = user?.role === "admin" || Boolean(user?.effective?.is_admin);
  const [vista, setVista] = useState("");                 // "" consolidado · id de subcuenta · "sin"
  const [filtro, setFiltro] = useState({ desde: "", hasta: "", branch_id: "", moneda: "", estado: "" });
  const [data, setData] = useState(null);
  const [sucursales, setSucursales] = useState([]);
  const [viendo, setViendo] = useState(null);
  const [asignar, setAsignar] = useState({});            // factura → subcuenta elegida

  const cargar = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/suppliers/${id}/perfil`, { params: { subcuenta_id: vista, ...filtro } });
      setData(d);
    } catch (e) { toast.error(apiErr(e)); }
  }, [id, vista, filtro]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { api.get("/branches").then(({ data: b }) => setSucursales(b || [])).catch(() => {}); }, []);

  const ver = async (facturaId) => {
    try { const { data: f } = await api.get(`/purchase-invoices/${facturaId}`); setViendo(f); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const clasificar = async (f) => {
    const sid = asignar[f.id];
    if (!sid) return toast.error("Elige la subcuenta");
    const motivo = window.prompt(`Asignar ${f.numero_interno} a ${data.subcuentas.find((x) => x.id === sid)?.nombre}.\nMotivo:`);
    if (!motivo || !motivo.trim()) return;
    try {
      await api.post(`/purchase-invoices/${f.id}/subcuenta`, { subcuenta_id: sid, motivo });
      toast.success(`${f.numero_interno} clasificada`); cargar();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!data) return <Layout title="Proveedor"><div className="p-10 text-center text-text-muted">Cargando…</div></Layout>;
  const sup = data.proveedor;
  const subs = data.subcuentas || [];
  const pend = data.pendientes_clasificacion;
  const consolidado = vista === "";
  const subDe = (doc) => (doc.subcuenta_codigo ? `${doc.subcuenta_codigo}` : "Pendiente de clasificación");
  const tabs = [{ k: "", l: "Consolidado" }, ...subs.map((x) => ({ k: x.id, l: `${x.pais_nombre} · ${x.codigo}` })),
    ...(pend ? [{ k: SIN, l: `Pendientes de clasificación (${pend.facturas})` }] : [])];

  return (
    <Layout title={sup.nombre} subtitle={`${sup.codigo || ""} · RTN ${sup.rtn || "—"} · perfil del proveedor`}
      actions={<button onClick={() => navigate("/proveedores")} className="px-3 py-2 text-sm border border-border rounded-md text-text-secondary flex items-center gap-2"><ArrowLeftIcon size={14} /> Proveedores</button>}>
      <div className="border-b border-border mb-4 flex gap-1 flex-wrap" data-testid="perfil-tabs">
        {tabs.map((t) => (
          <button key={t.k || "cons"} onClick={() => setVista(t.k)} data-testid={`perfil-tab-${t.k || "consolidado"}`}
            className={`px-4 py-2.5 text-sm relative ${vista === t.k ? "text-agro-green" : "text-text-secondary hover:text-text-primary"}`}>
            {t.l}{vista === t.k && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-agro-green" />}
          </button>
        ))}
      </div>

      {consolidado && subs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4" data-testid="perfil-subcuentas">
          {subs.map((x) => (
            <button key={x.id} onClick={() => setVista(x.id)} className="text-left bg-card border border-border/60 rounded-lg p-3 hover:border-agro-green/50" data-testid={`perfil-sub-${x.codigo}`}>
              <div className="font-semibold text-sm">{x.nombre}</div>
              <div className="text-[11px] text-text-muted font-mono">{x.codigo} · cuenta {x.cuenta_control}</div>
              <div className="text-[11px] text-text-muted">Auxiliares: {(x.auxiliares || []).map((a) => a.branch_codigo).join(", ") || "se crean al primer uso en cada sucursal"}</div>
              <div className="mt-1 text-xs">{Object.entries(x.por_moneda || {}).map(([m, v]) => (
                <div key={m}>Saldo {fmtMon(v.saldo, m)}{m !== "HNL" && <span className="text-text-muted"> · {fmtL(v.saldo_hnl)}</span>}</div>
              ))}{!Object.keys(x.por_moneda || {}).length && <span className="text-text-muted">Sin facturas</span>}</div>
            </button>
          ))}
        </div>
      )}

      <div className="bg-card border border-border/60 rounded-lg p-3 mb-4 flex flex-wrap gap-2 items-end text-xs" data-testid="perfil-filtros">
        <label>Desde<input type="date" value={filtro.desde} onChange={(e) => setFiltro({ ...filtro, desde: e.target.value })} className={`${inp} block`} /></label>
        <label>Hasta<input type="date" value={filtro.hasta} onChange={(e) => setFiltro({ ...filtro, hasta: e.target.value })} className={`${inp} block`} /></label>
        <label>Sucursal<select value={filtro.branch_id} onChange={(e) => setFiltro({ ...filtro, branch_id: e.target.value })} className={`${inp} block`} data-testid="perfil-f-sucursal">
          <option value="">Todas</option>{sucursales.map((b) => <option key={b.id} value={b.id}>{b.codigo} · {b.nombre}</option>)}</select></label>
        <label>Moneda<select value={filtro.moneda} onChange={(e) => setFiltro({ ...filtro, moneda: e.target.value })} className={`${inp} block`} data-testid="perfil-f-moneda">
          <option value="">Todas</option><option value="HNL">Lempiras</option><option value="USD">Dólares</option></select></label>
        <label>Estado<select value={filtro.estado} onChange={(e) => setFiltro({ ...filtro, estado: e.target.value })} className={`${inp} block`} data-testid="perfil-f-estado">
          <option value="">Todos</option>{Object.entries(ESTADO_PAGO).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4" data-testid="perfil-totales">
        {Object.entries(data.totales_por_moneda || {}).map(([m, t]) => (
          <div key={m} className="bg-card border border-border/60 rounded-lg p-4 text-sm" data-testid={`perfil-total-${m}`}>
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">{m === "USD" ? "Operaciones en dólares" : "Operaciones en lempiras"} · {t.facturas} factura(s)</div>
            <Fila l="Facturado" v={fmtMon(t.facturado, m)} extra={m !== "HNL" ? fmtL(t.facturado_hnl) : ""} />
            <Fila l="Pagado" v={fmtMon(t.pagado, m)} />
            <Fila l="Notas de crédito" v={fmtMon(t.notas_credito, m)} />
            <Fila l="Saldo por pagar" v={fmtMon(t.saldo, m)} extra={m !== "HNL" ? `${fmtL(t.saldo_hnl)} en CxP` : ""} fuerte />
          </div>
        ))}
        {!Object.keys(data.totales_por_moneda || {}).length && <div className="text-text-muted text-sm">Sin operaciones con estos filtros.</div>}
      </div>

      <Seccion titulo={`Compras y facturas (${data.facturas.length})`} testid="perfil-facturas">
        <thead><tr className="text-left text-[10px] uppercase text-text-muted"><th className="p-2">Factura</th>{consolidado && <th className="p-2">Subcuenta</th>}<th className="p-2">Sucursal</th>
          <th className="p-2">Fecha</th><th className="p-2 text-right">Total</th><th className="p-2 text-right">Saldo</th><th className="p-2">Estado</th><th className="p-2"></th></tr></thead>
        <tbody>{data.facturas.map((f) => (
          <tr key={f.id} className="border-t border-border/40" data-testid={`perfil-fact-${f.numero_interno}`}>
            <td className="p-2 font-mono text-xs text-agro-green">{f.numero_interno}<div className="text-[10px] text-text-muted">{f.numero_proveedor}</div></td>
            {consolidado && <td className={`p-2 text-xs ${f.subcuenta_codigo ? "" : "text-agro-yellow"}`}>{subDe(f)}</td>}
            <td className="p-2 text-xs">{f.branch_codigo || "—"}</td><td className="p-2 text-xs">{fmtDate(f.fecha)}</td>
            <td className="p-2 text-right text-xs">{fmtMon(f.total_moneda ?? f.total, f.moneda)}</td>
            <td className="p-2 text-right text-xs text-agro-yellow">{fmtMon(f.saldo_moneda ?? f.saldo_pendiente, f.moneda)}</td>
            <td className="p-2 text-xs">{ESTADO_PAGO[f.estado] || f.estado}</td>
            <td className="p-2 text-right whitespace-nowrap">
              {vista === SIN && esAdmin && f.estado !== "anulada" && (
                <span className="inline-flex gap-1 mr-2">
                  <select value={asignar[f.id] || ""} onChange={(e) => setAsignar({ ...asignar, [f.id]: e.target.value })} className={inp} data-testid={`perfil-asignar-${f.numero_interno}`}>
                    <option value="">Subcuenta…</option>{subs.map((x) => <option key={x.id} value={x.id}>{x.codigo}</option>)}</select>
                  <button onClick={() => clasificar(f)} className="px-2 py-1 bg-agro-green text-black rounded text-xs" data-testid={`perfil-clasificar-${f.numero_interno}`}>Asignar</button>
                </span>)}
              <button onClick={() => ver(f.id)} className="p-1 text-text-muted hover:text-agro-green" title="Ver factura" data-testid={`perfil-ver-${f.numero_interno}`}><EyeIcon size={15} /></button>
            </td>
          </tr>))}</tbody>
      </Seccion>

      <Seccion titulo={`Pagos y aplicaciones (${data.pagos.length})`} testid="perfil-pagos">
        <thead><tr className="text-left text-[10px] uppercase text-text-muted"><th className="p-2">Fecha</th><th className="p-2">Factura</th>{consolidado && <th className="p-2">Subcuenta</th>}
          <th className="p-2">Referencia</th><th className="p-2 text-right">Pagado</th><th className="p-2 text-right">Tasa</th><th className="p-2 text-right">Cancela</th><th className="p-2"></th></tr></thead>
        <tbody>{data.pagos.map((x) => (
          <tr key={x.id} className="border-t border-border/40">
            <td className="p-2 text-xs">{fmtDate(x.fecha)}</td><td className="p-2 font-mono text-xs">{x.factura_numero}</td>
            {consolidado && <td className="p-2 text-xs">{subDe(x)}</td>}
            <td className="p-2 text-xs">{x.referencia}<div className="text-[10px] text-text-muted">{x.metodo}</div></td>
            <td className="p-2 text-right text-xs">{fmtMon(x.monto_moneda_pago || x.monto, x.moneda_pago)}</td>
            <td className="p-2 text-right text-xs">{x.tipo_cambio_pago || "—"}</td>
            <td className="p-2 text-right text-xs">{fmtMon(x.cancelado_moneda_factura || x.monto, x.moneda_factura)}</td>
            <td className="p-2 text-right"><button onClick={() => ver(x.factura_id)} className="p-1 text-text-muted hover:text-agro-green" title="Ver factura" aria-label="Ver factura"><EyeIcon size={15} /></button></td>
          </tr>))}</tbody>
      </Seccion>

      <Seccion titulo={`Notas de crédito (${data.notas_credito.length})`} testid="perfil-ncs">
        <thead><tr className="text-left text-[10px] uppercase text-text-muted"><th className="p-2">Número</th><th className="p-2">Factura</th>{consolidado && <th className="p-2">Subcuenta</th>}
          <th className="p-2">Fecha</th><th className="p-2 text-right">Total</th><th className="p-2 text-right">Aplicado (L)</th><th className="p-2 text-right">Disponible</th><th className="p-2">Estado</th><th className="p-2"></th></tr></thead>
        <tbody>{data.notas_credito.map((n) => (
          <tr key={n.id} className="border-t border-border/40">
            <td className="p-2 font-mono text-xs">{n.numero}</td><td className="p-2 font-mono text-xs">{n.factura_numero}</td>
            {consolidado && <td className="p-2 text-xs">{subDe(n)}</td>}
            <td className="p-2 text-xs">{fmtDate(n.fecha)}</td>
            <td className="p-2 text-right text-xs">{fmtMon(n.total_moneda ?? n.total, n.moneda)}</td>
            <td className="p-2 text-right text-xs">{fmtL(n.aplicado_factura)}</td>
            <td className="p-2 text-right text-xs">{fmtL(n.saldo_disponible)}</td>
            <td className="p-2 text-xs">{n.estado}</td>
            <td className="p-2 text-right"><button onClick={() => ver(n.purchase_invoice_id)} className="p-1 text-text-muted hover:text-agro-green" title="Ver factura" aria-label="Ver factura"><EyeIcon size={15} /></button></td>
          </tr>))}</tbody>
      </Seccion>

      {viendo && <PurchaseInvoiceViewer inv={viendo} onClose={() => setViendo(null)}
        onPdf={() => openAuthPdf(`/purchase-invoices/${viendo.id}/pdf`)}
        onPay={() => navigate(`/cxp?factura=${viendo.id}`)} />}
    </Layout>
  );
}

const Fila = ({ l, v, extra, fuerte }) => (
  <div className="flex justify-between"><span className="text-text-secondary text-xs">{l}</span>
    <span className={fuerte ? "font-semibold text-agro-yellow" : ""}>{v}{extra && <span className="text-[10px] text-text-muted ml-1">({extra})</span>}</span></div>
);
const Seccion = ({ titulo, testid, children }) => (
  <div className="bg-card border border-border/60 rounded-lg mb-4 overflow-x-auto" data-testid={testid}>
    <div className="p-3 text-[10px] uppercase tracking-wider text-text-muted border-b border-border/60">{titulo}</div>
    <table className="w-full">{children}</table>
  </div>
);
